import React, { useState, useEffect } from 'react';
import {
  DownOutlined,
  SearchOutlined,
  GlobalOutlined,
  UserOutlined,
  UploadOutlined,
  EyeOutlined,
  PlusOutlined,
  MinusOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import { Select, Input, Slider, Switch, Button, Tag, Radio, Progress } from 'antd';
import { message } from 'antd';

// Mock data for languages
const languages = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'it', label: 'Italian' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'zh', label: 'Chinese' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
  { value: 'ar', label: 'Arabic' },
];

const MetaAudienceTargeting = ({ campaignData, onUpdate, onNext }) => {
  const [selectedLocations, setSelectedLocations] = useState(campaignData.metaAds?.selectedLocations || []);
  const [locationSearch, setLocationSearch] = useState('');
  const [radius, setRadius] = useState(campaignData.metaAds?.radius || campaignData.targetingRadius || 1);
  const [selectedLanguages, setSelectedLanguages] = useState(campaignData.metaAds?.selectedLanguages || []);
  const [ageRange, setAgeRange] = useState(campaignData.metaAds?.ageRange || [18, 65]);
  const [gender, setGender] = useState(campaignData.metaAds?.gender || 'all');
  const [customAudiences, setCustomAudiences] = useState(campaignData.metaAds?.customAudiences || []);
  const [detailedTargeting, setDetailedTargeting] = useState(campaignData.metaAds?.detailedTargeting || []);
  const [excludedTargeting, setExcludedTargeting] = useState(campaignData.metaAds?.excludedTargeting || []);
  const [advantageDetailedTargeting, setAdvantageDetailedTargeting] = useState(campaignData.metaAds?.advantageDetailedTargeting || false);
  const [customTargetingOptions, setCustomTargetingOptions] = useState(campaignData.metaAds?.customTargetingOptions || []);
  const [newCustomTarget, setNewCustomTarget] = useState('');
  const [newCustomTargetType, setNewCustomTargetType] = useState('interest');
  const [showCustomTargetInput, setShowCustomTargetInput] = useState(false);
  const [customInputForSection, setCustomInputForSection] = useState(null); // 'include' or 'exclude'

  // Meta Business targeting options
  const targetingCategories = {
    interests: [
      { value: 'business', label: 'Business', category: 'Interests' },
      { value: 'technology', label: 'Technology', category: 'Interests' },
      { value: 'entertainment', label: 'Entertainment', category: 'Interests' },
      { value: 'fitness', label: 'Fitness & Wellness', category: 'Interests' },
      { value: 'food', label: 'Food & Drink', category: 'Interests' },
      { value: 'travel', label: 'Travel', category: 'Interests' },
      { value: 'shopping', label: 'Shopping', category: 'Interests' },
      { value: 'sports', label: 'Sports', category: 'Interests' },
      { value: 'music', label: 'Music', category: 'Interests' },
      { value: 'gaming', label: 'Gaming', category: 'Interests' },
      { value: 'fashion', label: 'Fashion', category: 'Interests' },
      { value: 'automotive', label: 'Automotive', category: 'Interests' },
      { value: 'real_estate', label: 'Real Estate', category: 'Interests' },
      { value: 'finance', label: 'Finance', category: 'Interests' },
      { value: 'education', label: 'Education', category: 'Interests' },
      { value: 'parenting', label: 'Parenting', category: 'Interests' },
      { value: 'pets', label: 'Pets', category: 'Interests' },
      { value: 'outdoors', label: 'Outdoors', category: 'Interests' },
      { value: 'other_interest', label: 'Other (Add Custom)', category: 'Interests', isOther: true },
    ],
    behaviors: [
      { value: 'engaged_shoppers', label: 'Engaged Shoppers', category: 'Behaviors' },
      { value: 'frequent_travelers', label: 'Frequent Travelers', category: 'Behaviors' },
      { value: 'business_travelers', label: 'Business Travelers', category: 'Behaviors' },
      { value: 'small_business_owners', label: 'Small Business Owners', category: 'Behaviors' },
      { value: 'mobile_purchasers', label: 'Mobile Purchasers', category: 'Behaviors' },
      { value: 'online_purchasers', label: 'Online Purchasers', category: 'Behaviors' },
      { value: 'luxury_buyers', label: 'Luxury Buyers', category: 'Behaviors' },
      { value: 'auto_buyers', label: 'Auto Buyers', category: 'Behaviors' },
      { value: 'tech_early_adopters', label: 'Technology Early Adopters', category: 'Behaviors' },
      { value: 'fitness_enthusiasts', label: 'Fitness Enthusiasts', category: 'Behaviors' },
      { value: 'restaurant_goers', label: 'Restaurant Goers', category: 'Behaviors' },
      { value: 'event_goers', label: 'Event Goers', category: 'Behaviors' },
      { value: 'streaming_viewers', label: 'Streaming Viewers', category: 'Behaviors' },
      { value: 'other_behavior', label: 'Other (Add Custom)', category: 'Behaviors', isOther: true },
    ],
    demographics: [
      { value: 'parents', label: 'Parents', category: 'Demographics' },
      { value: 'new_parents', label: 'New Parents', category: 'Demographics' },
      { value: 'homeowners', label: 'Homeowners', category: 'Demographics' },
      { value: 'renters', label: 'Renters', category: 'Demographics' },
      { value: 'college_students', label: 'College Students', category: 'Demographics' },
      { value: 'college_graduates', label: 'College Graduates', category: 'Demographics' },
      { value: 'business_owners', label: 'Business Owners', category: 'Demographics' },
      { value: 'managers', label: 'Managers', category: 'Demographics' },
      { value: 'executives', label: 'Executives', category: 'Demographics' },
      { value: 'newlyweds', label: 'Newlyweds', category: 'Demographics' },
      { value: 'veterans', label: 'Veterans', category: 'Demographics' },
      { value: 'donors', label: 'Donors', category: 'Demographics' },
      { value: 'other_demographic', label: 'Other (Add Custom)', category: 'Demographics', isOther: true },
    ],
  };

  // Flatten all targeting options for Select (including custom ones)
  const allTargetingOptions = [
    ...targetingCategories.interests.map(i => ({ ...i, type: 'interest' })),
    ...targetingCategories.behaviors.map(b => ({ ...b, type: 'behavior' })),
    ...targetingCategories.demographics.map(d => ({ ...d, type: 'demographic' })),
    ...customTargetingOptions.map(c => ({ ...c, type: c.type || 'custom' })),
  ];

  // Get Geotab stop/route locations from Step 4
  const geotabStops = campaignData.selectedStopsData || [];
  const geotabRoutes = campaignData.selectedRoutesData || [];
  
  // Convert Geotab stops to location options for Meta targeting
  const geotabStopLocations = geotabStops.map((stop, index) => ({
    value: `geotab_stop_${stop.id}`,
    label: stop.address || `${stop.coords?.lat?.toFixed(4)}, ${stop.coords?.lng?.toFixed(4)}`,
    type: 'geotab_stop',
    coords: stop.coords,
    city: stop.city,
    state: stop.state,
    postcode: stop.postcode,
    stopData: stop
  }));

  // Convert Geotab routes to location options (start and end points)
  const geotabRouteLocations = geotabRoutes.flatMap((route, index) => {
    const locations = [];
    
    // Add start location
    if (route.coordinates && route.coordinates.length > 0) {
      const startCoord = route.coordinates[0];
      locations.push({
        value: `geotab_route_${route.id}_start`,
        label: route.startAddress || `Start: ${startCoord[0]?.toFixed(4)}, ${startCoord[1]?.toFixed(4)}`,
        type: 'geotab_route_start',
        coords: { lat: startCoord[0], lng: startCoord[1] },
        routeData: route
      });
      
      // Add end location
      const endCoord = route.coordinates[route.coordinates.length - 1];
      locations.push({
        value: `geotab_route_${route.id}_end`,
        label: route.endAddress || `End: ${endCoord[0]?.toFixed(4)}, ${endCoord[1]?.toFixed(4)}`,
        type: 'geotab_route_end',
        coords: { lat: endCoord[0], lng: endCoord[1] },
        routeData: route
      });
    }
    
    return locations;
  });

  // Combine all Geotab locations (stops for stop campaigns, routes for route campaigns)
  const geotabLocations = [...geotabStopLocations, ...geotabRouteLocations];

  // Auto-populate locations from Geotab stops/routes on first load
  useEffect(() => {
    if (geotabLocations.length > 0 && selectedLocations.length === 0) {
      const autoLocations = geotabLocations.map(loc => ({
        ...loc,
        id: Date.now() + Math.random(),
        include: true,
        radius: radius
      }));
      setSelectedLocations(autoLocations);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geotabStops.length, geotabRoutes.length]);

  // Calculate estimated audience size (mock calculation)
  const calculateAudienceSize = () => {
    let baseSize = 1000000; // Base audience size

    // Adjust for locations
    if (selectedLocations.length > 0) {
      baseSize = baseSize * (selectedLocations.length * 0.1);
    }

    // Adjust for languages
    if (selectedLanguages.length > 0) {
      baseSize = baseSize * (selectedLanguages.length * 0.8);
    }

    // Adjust for age range
    const ageSpan = ageRange[1] - ageRange[0];
    baseSize = baseSize * (ageSpan / 50); // Normalize to typical age span

    // Adjust for gender
    if (gender !== 'all') {
      baseSize = baseSize * 0.5;
    }

    return Math.round(baseSize);
  };

  const audienceSize = calculateAudienceSize();

  // Determine audience size indicator
  const getAudienceSizeIndicator = () => {
    if (audienceSize < 10000) return { label: 'Too Narrow', color: 'red', icon: <ExclamationCircleOutlined /> };
    if (audienceSize > 10000000) return { label: 'Too Broad', color: 'orange', icon: <WarningOutlined /> };
    return { label: 'Balanced', color: 'green', icon: <CheckCircleOutlined /> };
  };

  const audienceIndicator = getAudienceSizeIndicator();

  // Keep parent data in sync as fields change (single-page campaign view)
  useEffect(() => {
    onUpdate({
      metaAds: {
        selectedLocations,
        radius,
        selectedLanguages,
        ageRange,
        gender,
        customAudiences,
        detailedTargeting,
        excludedTargeting,
        advantageDetailedTargeting,
        customTargetingOptions,
      }
    });
  }, [selectedLocations, radius, selectedLanguages, ageRange, gender, customAudiences, detailedTargeting, excludedTargeting, advantageDetailedTargeting, customTargetingOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleContinue = () => {
    if (selectedLocations.length === 0) {
      message.warning('Please select at least one location');
      return;
    }

    onUpdate({
      metaAds: {
        ...campaignData.metaAds,
        selectedLocations,
        radius,
        selectedLanguages,
        ageRange,
        gender,
        customAudiences,
        detailedTargeting,
        excludedTargeting,
        advantageDetailedTargeting,
        customTargetingOptions,
      }
    });
    onNext();
  };

  const addLocation = (location, include = true) => {
    const newLocation = {
      ...location,
      id: Date.now(),
      include,
      radius: location.type === 'city' || location.type === 'zip' ? radius : null
    };
    setSelectedLocations([...selectedLocations, newLocation]);
  };

  const removeLocation = (locationId) => {
    setSelectedLocations(selectedLocations.filter(loc => loc.id !== locationId));
  };

  const addCustomAudience = (type, config = {}) => {
    const newAudience = {
      id: Date.now(),
      type,
      ...config
    };
    setCustomAudiences([...customAudiences, newAudience]);
  };

  const removeCustomAudience = (audienceId) => {
    setCustomAudiences(customAudiences.filter(aud => aud.id !== audienceId));
  };

  // Detailed targeting handlers
  const handleIncludeTargeting = (selectedValues) => {
    // Check if 'other' option was selected
    const hasOther = selectedValues.some(v => v.startsWith('other_'));
    if (hasOther) {
      // Determine which type of "other" was selected
      const otherValue = selectedValues.find(v => v.startsWith('other_'));
      if (otherValue === 'other_interest') setNewCustomTargetType('interest');
      else if (otherValue === 'other_behavior') setNewCustomTargetType('behavior');
      else if (otherValue === 'other_demographic') setNewCustomTargetType('demographic');
      
      setCustomInputForSection('include');
      setShowCustomTargetInput(true);
      setNewCustomTarget('');
      // Remove 'other' from selection for now
      const filtered = selectedValues.filter(v => !v.startsWith('other_'));
      const selected = filtered.map(val => {
        const option = allTargetingOptions.find(o => o.value === val);
        return option || { value: val, label: val };
      });
      setDetailedTargeting(selected);
      return;
    }
    
    const selected = selectedValues.map(val => {
      const option = allTargetingOptions.find(o => o.value === val);
      return option || { value: val, label: val };
    });
    setDetailedTargeting(selected);
  };

  const handleExcludeTargeting = (selectedValues) => {
    // Check if 'other' option was selected
    const hasOther = selectedValues.some(v => v.startsWith('other_'));
    if (hasOther) {
      // Determine which type of "other" was selected
      const otherValue = selectedValues.find(v => v.startsWith('other_'));
      if (otherValue === 'other_interest') setNewCustomTargetType('interest');
      else if (otherValue === 'other_behavior') setNewCustomTargetType('behavior');
      else if (otherValue === 'other_demographic') setNewCustomTargetType('demographic');
      
      setCustomInputForSection('exclude');
      setShowCustomTargetInput(true);
      setNewCustomTarget('');
      // Remove 'other' from selection for now
      const filtered = selectedValues.filter(v => !v.startsWith('other_'));
      const selected = filtered.map(val => {
        const option = allTargetingOptions.find(o => o.value === val);
        return option || { value: val, label: val };
      });
      setExcludedTargeting(selected);
      return;
    }
    
    const selected = selectedValues.map(val => {
      const option = allTargetingOptions.find(o => o.value === val);
      return option || { value: val, label: val };
    });
    setExcludedTargeting(selected);
  };

  const addCustomTargetOption = () => {
    if (!newCustomTarget.trim()) return;
    
    const newOption = {
      value: `custom_${Date.now()}`,
      label: newCustomTarget.trim(),
      type: newCustomTargetType,
      category: newCustomTargetType.charAt(0).toUpperCase() + newCustomTargetType.slice(1),
      isCustom: true,
    };
    
    // Add to custom options list
    setCustomTargetingOptions([...customTargetingOptions, newOption]);
    
    // Add directly to the appropriate targeting section
    if (customInputForSection === 'include') {
      setDetailedTargeting([...detailedTargeting, newOption]);
    } else if (customInputForSection === 'exclude') {
      setExcludedTargeting([...excludedTargeting, newOption]);
    }
    
    setNewCustomTarget('');
    setShowCustomTargetInput(false);
    setCustomInputForSection(null);
    message.success(`Added: ${newOption.label}`);
  };

  const cancelCustomInput = () => {
    setNewCustomTarget('');
    setShowCustomTargetInput(false);
    setCustomInputForSection(null);
  };

  const removeIncludedTarget = (value) => {
    setDetailedTargeting(detailedTargeting.filter(t => t.value !== value));
  };

  const removeExcludedTarget = (value) => {
    setExcludedTargeting(excludedTargeting.filter(t => t.value !== value));
  };

  const removeCustomTargetOption = (value) => {
    setCustomTargetingOptions(customTargetingOptions.filter(t => t.value !== value));
    // Also remove from selected targeting if present
    setDetailedTargeting(detailedTargeting.filter(t => t.value !== value));
    setExcludedTargeting(excludedTargeting.filter(t => t.value !== value));
  };

  return (
    <div className="step-container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px 100px' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '16px',
          background: '#f3f4f6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '24px',
        }}>
          <UserOutlined style={{ fontSize: '32px', color: '#111827' }} />
        </div>

        <h1 style={{
          fontSize: '32px',
          fontWeight: 700,
          color: '#000',
          marginBottom: '12px',
          textAlign: 'center'
        }}>
          Audience & Targeting
        </h1>
        <p style={{
          fontSize: '16px',
          color: 'var(--ink-200)',
          marginBottom: 0,
          textAlign: 'center'
        }}>
          Define who will see your ads
        </p>
      </div>

      {/* All rows share the same 2-column grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'start' }}>
      {/* Row 1: Locations | Custom Audiences */}
      <div style={{ display: 'contents' }}>
      {/* Locations Section */}
      <div style={{
        background: '#fff',
        border: '1px solid var(--paper-300)',
        borderRadius: '12px',
        padding: '24px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: '#eff6ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <EnvironmentOutlined style={{ fontSize: '20px', color: 'var(--ink)' }} />
          </div>
          <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', margin: 0 }}>
            Locations
          </h3>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <Input
            placeholder="Search by Country, Region, City, ZIP, or DMA"
            value={locationSearch}
            onChange={(e) => setLocationSearch(e.target.value)}
            prefix={<SearchOutlined />}
            size="large"
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '14px', color: 'var(--ink-200)', marginBottom: '8px' }}>
            Locations from Geotab (Step 4):
          </div>
          {geotabLocations.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {geotabLocations
                .filter(loc => loc.label.toLowerCase().includes(locationSearch.toLowerCase()))
                .map(location => (
                  <div key={location.value} style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <Button
                      size="small"
                      onClick={() => addLocation(location, true)}
                      disabled={selectedLocations.some(loc => loc.value === location.value && loc.include)}
                    >
                      <PlusOutlined /> Include
                    </Button>
                    <Button
                      size="small"
                      danger
                      onClick={() => addLocation(location, false)}
                      disabled={selectedLocations.some(loc => loc.value === location.value && !loc.include)}
                    >
                      <MinusOutlined /> Exclude
                    </Button>
                    <span style={{ fontSize: '12px', color: 'var(--ink-200)', alignSelf: 'center', marginLeft: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <EnvironmentOutlined />
                      {location.label}
                      {location.type === 'geotab_route_start' && <Tag color="green" style={{ marginLeft: 4, fontSize: 10 }}>Start</Tag>}
                      {location.type === 'geotab_route_end' && <Tag color="orange" style={{ marginLeft: 4, fontSize: 10 }}>End</Tag>}
                    </span>
                  </div>
                ))}
            </div>
          ) : (
            <div style={{ padding: '16px', background: '#fef3c7', borderRadius: '8px', color: '#92400e' }}>
              <WarningOutlined style={{ marginRight: '8px' }} />
              No Geotab locations selected in Step 4. Please go back and select stops or routes to target locations.
            </div>
          )}
        </div>

        {(selectedLocations.some(loc => loc.type === 'geotab_stop' || loc.type === 'geotab_route_start' || loc.type === 'geotab_route_end' || loc.type === 'city' || loc.type === 'zip')) && (
          <div style={{ marginTop: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
              Radius for Cities/ZIPs: {radius}mi
            </label>
            <Slider
              min={0.5}
              max={5}
              step={0.5}
              value={radius}
              onChange={setRadius}
              marks={{ 0.5: '0.5mi', 2.5: '2.5mi', 5: '5mi' }}
            />
          </div>
        )}

        <div style={{ marginTop: '16px' }}>
          <div style={{ fontSize: '14px', color: 'var(--ink-200)', marginBottom: '8px' }}>
            Selected Locations:
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {selectedLocations.map(location => (
              <Tag
                key={location.id}
                closable
                onClose={() => removeLocation(location.id)}
                color={location.include ? 'blue' : 'red'}
              >
                {location.include ? '+' : '-'} {location.label}
                {location.radius && ` (${location.radius}mi)`}
              </Tag>
            ))}
          </div>
        </div>
      </div>

      {/* Custom Audiences - second column in Row 1 */}
      <div style={{
        background: '#fff',
        border: '1px solid var(--paper-300)',
        borderRadius: '12px',
        padding: '24px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: '#fdf4ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <UploadOutlined style={{ fontSize: '20px', color: '#a855f7' }} />
          </div>
          <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', margin: 0 }}>
            Custom Audiences
          </h3>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
          <Button onClick={() => addCustomAudience('customer_list')} icon={<UploadOutlined />} block>
            Upload Customer List
          </Button>
          <Button onClick={() => addCustomAudience('website_visitors')} icon={<GlobalOutlined />} block>
            Website Visitors
          </Button>
          <Button onClick={() => addCustomAudience('app_activity')} icon={<EyeOutlined />} block>
            App Activity
          </Button>
          <Button onClick={() => addCustomAudience('lookalike')} icon={<UserOutlined />} block>
            Lookalike Audience
          </Button>
          <Button onClick={() => addCustomAudience('saved_audience')} icon={<CheckCircleOutlined />} block>
            Saved Audience
          </Button>
        </div>

        <div style={{ marginTop: '16px' }}>
          <div style={{ fontSize: '14px', color: 'var(--ink-200)', marginBottom: '8px' }}>
            Selected Custom Audiences:
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {customAudiences.map(audience => (
              <Tag
                key={audience.id}
                closable
                onClose={() => removeCustomAudience(audience.id)}
                color="purple"
              >
                {audience.type === 'customer_list' && '📤 Customer List'}
                {audience.type === 'website_visitors' && '🌐 Website Visitors'}
                {audience.type === 'app_activity' && '📱 App Activity'}
                {audience.type === 'lookalike' && '👥 Lookalike Audience'}
                {audience.type === 'saved_audience' && '💾 Saved Audience'}
              </Tag>
            ))}
          </div>
        </div>
      </div>
      </div>{/* end Row 1 display:contents */}

      {/* Row 2: Demographics — spans both columns */}
      <div style={{
        gridColumn: '1 / -1',
        background: '#fff',
        border: '1px solid var(--paper-300)',
        borderRadius: '12px',
        padding: '20px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <UserOutlined style={{ fontSize: '18px', color: '#f59e0b' }} />
          </div>
          <h4 style={{ fontSize: '16px', fontWeight: 600, color: '#111827', margin: 0 }}>Demographics</h4>
        </div>
        {/* Two-column inner layout */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
                Age Range: {ageRange[0]} - {ageRange[1] === 65 ? '65+' : ageRange[1]}
              </label>
              <Slider range min={18} max={65} value={ageRange} onChange={setAgeRange} marks={{ 18: '18', 35: '35', 50: '50', 65: '65+' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>Gender</label>
              <Radio.Group value={gender} onChange={(e) => setGender(e.target.value)}>
                <Radio value="all">All</Radio>
                <Radio value="men">Men</Radio>
                <Radio value="women">Women</Radio>
              </Radio.Group>
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>Languages</label>
            <Select mode="multiple" placeholder="Select languages" value={selectedLanguages} onChange={setSelectedLanguages} style={{ width: '100%' }} size="large" options={languages} />
          </div>
        </div>
      </div>{/* end Demographics */}

      {/* Row 3: Detailed Targeting | Estimated Daily Reach */}
      <div style={{ display: 'contents' }}>
      {/* Detailed Targeting */}
      <div style={{
        background: '#fff',
        border: '1px solid var(--paper-300)',
        borderRadius: '12px',
        padding: '24px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: '#fef2f2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <EyeOutlined style={{ fontSize: '20px', color: '#ef4444' }} />
          </div>
          <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', margin: 0 }}>
            Detailed Targeting
          </h3>
        </div>

        {/* Include Section */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
            Include people who match (interests, behaviors, demographics)
          </label>
          <Select
            mode="multiple"
            placeholder="Search interests, behaviors, demographics..."
            value={detailedTargeting.map(t => t.value)}
            onChange={handleIncludeTargeting}
            style={{ width: '100%' }}
            size="large"
            filterOption={(input, option) => {
              const label = option.children?.props?.children || option.label || '';
              return label.toLowerCase().includes(input.toLowerCase());
            }}
          >
            <Select.OptGroup label="Interests">
              {targetingCategories.interests.map(item => (
                <Select.Option key={item.value} value={item.value}>
                  <span>{item.label}</span>
                </Select.Option>
              ))}
            </Select.OptGroup>
            <Select.OptGroup label="Behaviors">
              {targetingCategories.behaviors.map(item => (
                <Select.Option key={item.value} value={item.value}>
                  <span>{item.label}</span>
                </Select.Option>
              ))}
            </Select.OptGroup>
            <Select.OptGroup label="Demographics">
              {targetingCategories.demographics.map(item => (
                <Select.Option key={item.value} value={item.value}>
                  <span>{item.label}</span>
                </Select.Option>
              ))}
            </Select.OptGroup>
            {customTargetingOptions.length > 0 && (
              <Select.OptGroup label="Custom Options">
                {customTargetingOptions.map(item => (
                  <Select.Option key={item.value} value={item.value}>
                    <span>{item.label} (Custom)</span>
                  </Select.Option>
                ))}
              </Select.OptGroup>
            )}
          </Select>
          
          {/* Selected Include Tags */}
          {detailedTargeting.length > 0 && (
            <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {detailedTargeting.map(target => (
                <Tag
                  key={target.value}
                  closable
                  onClose={() => removeIncludedTarget(target.value)}
                  color="blue"
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  <span style={{ fontSize: '10px', color: '#60a5fa' }}>{target.type?.toUpperCase() || 'TARGET'}</span>
                  <span>{target.label}</span>
                </Tag>
              ))}
            </div>
          )}
          
          {/* Custom Input for Include Section */}
          {showCustomTargetInput && customInputForSection === 'include' && (
            <div style={{
              marginTop: '12px',
              padding: '12px',
              background: '#eff6ff',
              borderRadius: '8px',
              border: '1px solid #bfdbfe',
            }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <Select
                  value={newCustomTargetType}
                  onChange={setNewCustomTargetType}
                  style={{ width: 120 }}
                  size="small"
                >
                  <Select.Option value="interest">Interest</Select.Option>
                  <Select.Option value="behavior">Behavior</Select.Option>
                  <Select.Option value="demographic">Demographic</Select.Option>
                </Select>
                <Input
                  placeholder="Enter custom targeting..."
                  value={newCustomTarget}
                  onChange={(e) => setNewCustomTarget(e.target.value)}
                  style={{ flex: 1, minWidth: 200 }}
                  size="small"
                  onPressEnter={addCustomTargetOption}
                  autoFocus
                />
                <Button type="primary" size="small" onClick={addCustomTargetOption} disabled={!newCustomTarget.trim()}>
                  Add
                </Button>
                <Button size="small" onClick={cancelCustomInput}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Exclude Section */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
            Exclude people who match
          </label>
          <Select
            mode="multiple"
            placeholder="Search interests, behaviors, demographics to exclude..."
            value={excludedTargeting.map(t => t.value)}
            onChange={handleExcludeTargeting}
            style={{ width: '100%' }}
            size="large"
            filterOption={(input, option) => {
              const label = option.children?.props?.children || option.label || '';
              return label.toLowerCase().includes(input.toLowerCase());
            }}
          >
            <Select.OptGroup label="Interests">
              {targetingCategories.interests.map(item => (
                <Select.Option key={item.value} value={item.value}>
                  <span>{item.label}</span>
                </Select.Option>
              ))}
            </Select.OptGroup>
            <Select.OptGroup label="Behaviors">
              {targetingCategories.behaviors.map(item => (
                <Select.Option key={item.value} value={item.value}>
                  <span>{item.label}</span>
                </Select.Option>
              ))}
            </Select.OptGroup>
            <Select.OptGroup label="Demographics">
              {targetingCategories.demographics.map(item => (
                <Select.Option key={item.value} value={item.value}>
                  <span>{item.label}</span>
                </Select.Option>
              ))}
            </Select.OptGroup>
            {customTargetingOptions.length > 0 && (
              <Select.OptGroup label="Custom Options">
                {customTargetingOptions.map(item => (
                  <Select.Option key={item.value} value={item.value}>
                    <span>{item.label} (Custom)</span>
                  </Select.Option>
                ))}
              </Select.OptGroup>
            )}
          </Select>
          
          {/* Selected Exclude Tags */}
          {excludedTargeting.length > 0 && (
            <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {excludedTargeting.map(target => (
                <Tag
                  key={target.value}
                  closable
                  onClose={() => removeExcludedTarget(target.value)}
                  color="red"
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  <span style={{ fontSize: '10px', color: '#f87171' }}>{target.type?.toUpperCase() || 'TARGET'}</span>
                  <span>{target.label}</span>
                </Tag>
              ))}
            </div>
          )}
          
          {/* Custom Input for Exclude Section */}
          {showCustomTargetInput && customInputForSection === 'exclude' && (
            <div style={{
              marginTop: '12px',
              padding: '12px',
              background: '#fef2f2',
              borderRadius: '8px',
              border: '1px solid #fecaca',
            }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <Select
                  value={newCustomTargetType}
                  onChange={setNewCustomTargetType}
                  style={{ width: 120 }}
                  size="small"
                >
                  <Select.Option value="interest">Interest</Select.Option>
                  <Select.Option value="behavior">Behavior</Select.Option>
                  <Select.Option value="demographic">Demographic</Select.Option>
                </Select>
                <Input
                  placeholder="Enter custom targeting to exclude..."
                  value={newCustomTarget}
                  onChange={(e) => setNewCustomTarget(e.target.value)}
                  style={{ flex: 1, minWidth: 200 }}
                  size="small"
                  onPressEnter={addCustomTargetOption}
                  autoFocus
                />
                <Button type="primary" size="small" onClick={addCustomTargetOption} disabled={!newCustomTarget.trim()}>
                  Add
                </Button>
                <Button size="small" onClick={cancelCustomInput}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Custom Options List */}
        {customTargetingOptions.length > 0 && (
          <div style={{
            padding: '12px',
            background: '#fefce8',
            borderRadius: '8px',
            border: '1px solid #fef08a',
            marginBottom: '16px',
          }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#a16207', marginBottom: '8px' }}>
              Your Custom Targeting Options:
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {customTargetingOptions.map(option => (
                <Tag
                  key={option.value}
                  closable
                  onClose={() => removeCustomTargetOption(option.value)}
                  color="gold"
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  <span style={{ fontSize: '10px', color: '#b45309' }}>{option.type?.toUpperCase()}</span>
                  <span>{option.label}</span>
                </Tag>
              ))}
            </div>
          </div>
        )}

        <div style={{
          padding: '16px',
          background: advantageDetailedTargeting ? '#eff6ff' : '#f8fafc',
          borderRadius: '8px',
          border: advantageDetailedTargeting ? '1px solid var(--ink)' : '1px solid #e2e8f0',
          transition: 'all 0.3s ease',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '6px',
                background: advantageDetailedTargeting ? '#dbeafe' : '#f3f4f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
              }}>
                <UserOutlined style={{ fontSize: '16px', color: advantageDetailedTargeting ? 'var(--ink)' : 'var(--ink-200)' }} />
              </div>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '2px' }}>
                  Advantage+ Detailed Targeting
                </div>
                <div style={{ fontSize: '12px', color: 'var(--ink-200)' }}>
                  Use AI to optimize your targeting for better results
                </div>
              </div>
            </div>
            <Switch
              checked={advantageDetailedTargeting}
              onChange={setAdvantageDetailedTargeting}
              style={{
                background: advantageDetailedTargeting ? 'var(--ink)' : 'var(--paper-300)',
              }}
            />
          </div>
          
          {/* Show expanded explanation when enabled */}
          {advantageDetailedTargeting && (
            <div style={{
              marginTop: '12px',
              padding: '12px',
              background: '#dbeafe',
              borderRadius: '6px',
              border: '1px solid #93c5fd',
            }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e40af', marginBottom: '8px' }}>
                ✓ Advantage+ Detailed Targeting is ON
              </div>
              <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12px', color: '#1e40af' }}>
                <li>Meta will expand your targeting to reach more people likely to achieve your objective</li>
                <li>Your detailed targeting selections become suggestions rather than strict filters</li>
                <li>Helps find conversions beyond your specified audience criteria</li>
                {(detailedTargeting.length > 0 || excludedTargeting.length > 0) && (
                  <li style={{ marginTop: '4px', fontWeight: 600 }}>
                    Based on your {detailedTargeting.length} include and {excludedTargeting.length} exclude criteria
                  </li>
                )}
              </ul>
            </div>
          )}
          
          {/* Show warning when disabled with targeting set */}
          {!advantageDetailedTargeting && (detailedTargeting.length > 0 || excludedTargeting.length > 0) && (
            <div style={{
              marginTop: '12px',
              padding: '10px',
              background: '#fef3c7',
              borderRadius: '6px',
              border: '1px solid #fcd34d',
              fontSize: '12px',
              color: '#92400e',
            }}>
              💡 <strong>Tip:</strong> Enable Advantage+ to let Meta find more people similar to your targeting criteria
            </div>
          )}
        </div>
      </div>

      {/* Estimated Daily Reach */}
      <div style={{
        background: '#fff',
        border: '1px solid var(--paper-300)',
        borderRadius: '12px',
        padding: '24px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <UserOutlined style={{ fontSize: '20px', color: '#10b981' }} />
          </div>
          <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', margin: 0 }}>Estimated Daily Reach</h3>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
          <div style={{
            width: '56px', height: '56px', borderRadius: '14px',
            background: audienceIndicator.color === 'green' ? '#dcfce7' : audienceIndicator.color === 'orange' ? '#fef3c7' : '#fee2e2',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {audienceIndicator.icon}
          </div>
          <div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#111827', lineHeight: 1 }}>
              {audienceSize.toLocaleString()}
            </div>
            <div style={{ fontSize: '13px', color: 'var(--ink-200)', marginTop: '4px' }}>people per day</div>
          </div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ fontSize: '13px', color: 'var(--ink-200)' }}>Audience Size</span>
            <Tag color={audienceIndicator.color}>{audienceIndicator.label}</Tag>
          </div>
          <Progress
            percent={Math.min((audienceSize / 10000000) * 100, 100)}
            showInfo={false}
            strokeColor={audienceIndicator.color === 'green' ? '#10b981' : audienceIndicator.color === 'orange' ? '#f59e0b' : '#ef4444'}
            style={{ marginBottom: 0 }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
            <span style={{ fontSize: '11px', color: '#9ca3af' }}>Narrow</span>
            <span style={{ fontSize: '11px', color: '#9ca3af' }}>Broad</span>
          </div>
        </div>

        <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '8px', fontWeight: 600 }}>Targeting Summary</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
              <span style={{ color: 'var(--ink-200)' }}>Locations</span>
              <span style={{ fontWeight: 500 }}>{selectedLocations.length}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
              <span style={{ color: 'var(--ink-200)' }}>Age Range</span>
              <span style={{ fontWeight: 500 }}>{ageRange[0]}–{ageRange[1] === 65 ? '65+' : ageRange[1]}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
              <span style={{ color: 'var(--ink-200)' }}>Gender</span>
              <span style={{ fontWeight: 500, textTransform: 'capitalize' }}>{gender}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
              <span style={{ color: 'var(--ink-200)' }}>Interests</span>
              <span style={{ fontWeight: 500 }}>{detailedTargeting.length}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
              <span style={{ color: 'var(--ink-200)' }}>Exclusions</span>
              <span style={{ fontWeight: 500 }}>{excludedTargeting.length}</span>
            </div>
          </div>
        </div>
      </div>
      </div>{/* end Row 3 display:contents */}
      </div>{/* end outer 2-column grid */}

      {/* Footer */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: '#fff',
        borderTop: '1px solid var(--paper-300)',
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 10
      }}>
        <div style={{ fontSize: '14px', color: 'var(--ink-200)' }}>
          Building: Audience & Targeting
        </div>
        <button
          onClick={handleContinue}
          style={{
            padding: '12px 32px',
            background: selectedLocations.length > 0 ? 'var(--ink-200)' : 'var(--paper-300)',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '15px',
            fontWeight: 600,
            cursor: selectedLocations.length > 0 ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            if (selectedLocations.length > 0) {
              e.currentTarget.style.background = '#4b5563';
            }
          }}
          onMouseLeave={(e) => {
            if (selectedLocations.length > 0) {
              e.currentTarget.style.background = 'var(--ink-200)';
            }
          }}
        >
          Continue
          <DownOutlined />
        </button>
      </div>
    </div>
  );
};

export default MetaAudienceTargeting;